import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import { PROCUREMENT_LINKS } from './procurementLinks';
import { documentLinkProps } from '../templateFile';
import '../initiation/PocTypePage.scss';
import apiService from '../../service/ApiService';

type LocationState = {
  pocId?: string;
  procurement?: {
    noCommitmentDeclared: boolean;
    sourcingWillFollowDeclared: boolean;
    complianceConfirmed: boolean;
  };
  [key: string]: unknown;
};

const ProcurementAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;

  const [noCommitmentDeclared, setNoCommitmentDeclared] = useState(
    state.procurement?.noCommitmentDeclared ?? false
  );
  const [sourcingWillFollowDeclared, setSourcingWillFollowDeclared] = useState(
    state.procurement?.sourcingWillFollowDeclared ?? false
  );
  const [complianceConfirmed, setComplianceConfirmed] = useState(
    state.procurement?.complianceConfirmed ?? false
  );
  const [attempted, setAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const bothDeclared =
    noCommitmentDeclared && sourcingWillFollowDeclared;

  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    if (!noCommitmentDeclared || !sourcingWillFollowDeclared) {
      next.declarations =
        'Tick both declaration boxes before continuing.';
    }
    if (!complianceConfirmed) {
      next.compliance =
        'Confirm that you have complied with the recommendations.';
    }
    return next;
  }, [noCommitmentDeclared, sourcingWillFollowDeclared, complianceConfirmed]);

  const isValid = Object.keys(errors).length === 0;

  const procurementPayload = {
    noCommitmentDeclared,
    sourcingWillFollowDeclared,
    complianceConfirmed,
  };

  const handleNext = async () => {
    setAttempted(true);
    setSubmitError(null);
    if (!isValid) return;
  
    if (!pocId) {
      setSubmitError('Missing PoC id. Complete Initiation first.');
      return;
    }
  
    setIsSubmitting(true);
    try {
      await apiService.saveProcurement(pocId, procurementPayload);
  
      navigate('/pocs/new/preparation/review', {
        state: {
          ...state,
          procurement: procurementPayload,
        },
      });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not save procurement. Check Network tab (PoC must be in Preparation).'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const registrationHref = PROCUREMENT_LINKS.supplierRegistration.href;

  return (
    <div className="initiation">
      <aside className="initiation__phases" aria-label="PoC phases">
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">1</span>
          <span>Initiation</span>
        </div>
        <div className="initiation__phase initiation__phase--active">
          <span className="initiation__phase-num">2</span>
          <span>Preparation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">3</span>
          <span>Running</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">4</span>
          <span>Evaluation</span>
        </div>
      </aside>

      <div className="initiation__main">
        <nav className="initiation__steps" aria-label="Preparation steps">
          <span className="initiation__step">Step 1</span>
          <span className="initiation__step">Step 2</span>
          <span className="initiation__step">Step 3</span>
          <span className="initiation__step">Step 4</span>
          <span className="initiation__step">Step 5</span>
          <span className="initiation__step">Step 6</span>
          <span className="initiation__step initiation__step--active">
            Step 7
          </span>
          <span className="initiation__step">Review and Submit</span>
        </nav>

        <Section theme="light" spacing="tight">
          <Eyebrow>POC Preparation</Eyebrow>
          <Headline as="h1" size="3xl">
            Procurement
          </Headline>
          <Lead>
            Step 7 — Complete the procurement checklist. This step is mandatory
            to finish Preparation.
          </Lead>

          <ol className="phase-gate__list" style={{ listStyle: 'decimal' }}>
            <li>
              You will be presented with a small checklist. Please tick the
              boxes to comply with the aligned track for procurement.
            </li>
            <li>
              To follow your local or group procurement path, you will be
              provided with a link to register your supplier in the respective
              procurement tool.
            </li>
            <li>
              At the end of the process, you will be asked for confirmation that
              you have been in compliance with the recommendations.
            </li>
          </ol>

          <div className="phase-gate">
            <p className="phase-gate__title">
              I hereby declare that as in scope of the PoC:
            </p>

            <label className="score-result__confirm">
              <input
                type="checkbox"
                checked={noCommitmentDeclared}
                onChange={(event) => {
                  setNoCommitmentDeclared(event.target.checked);
                }}
              />
              <span>
                No commercial commitments are made at this stage beyond the
                scope of the PoC.
              </span>
            </label>

            <label className="score-result__confirm">
              <input
                type="checkbox"
                checked={sourcingWillFollowDeclared}
                onChange={(event) => {
                  setSourcingWillFollowDeclared(event.target.checked);
                }}
              />
              <span>
                A sourcing request will be made if the PoC is successfully
                concluded and the initiative should be taken further.
              </span>
            </label>

            {attempted && errors.declarations ? (
              <p className="bj-field__error">{errors.declarations}</p>
            ) : null}
          </div>

          {bothDeclared ? (
            <div className="phase-gate" role="region" aria-label="Supplier registration">
              <p className="phase-gate__title">Supplier registration</p>
              <p>
                Register your supplier in your local/group procurement tool
                according to your known standards (Coupa etc.). You can find the
                link here.
              </p>
              <p>
                <a
                  className="scorecard__link"
                  {...documentLinkProps(registrationHref)}
                >
                  {PROCUREMENT_LINKS.supplierRegistration.label}
                </a>
              </p>
              <p>
                If you have questions, contact your local procurement owner and
                refer to the{' '}
                <a
                  className="scorecard__link"
                  {...documentLinkProps(
                    PROCUREMENT_LINKS.procurementStandard.href
                  )}
                >
                  {PROCUREMENT_LINKS.procurementStandard.label}
                </a>
                .
              </p>
            </div>
          ) : null}

          <label className="score-result__confirm">
            <input
              type="checkbox"
              checked={complianceConfirmed}
              onChange={(event) => {
                setComplianceConfirmed(event.target.checked);
              }}
            />
            <span>
              I confirm that I have been in compliance with the recommendations.
            </span>
          </label>
          {attempted && errors.compliance ? (
            <p className="bj-field__error">{errors.compliance}</p>
          ) : null}
          {submitError ? (
            <p className="bj-field__error">{submitError}</p>
          ) : null}

          <div className="initiation__actions">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() =>
                navigate('/pocs/new/preparation/legal', {
                  state: {
                    ...state,
                    procurement: procurementPayload,
                  },
                })
              }
            >
              ← Back to Legal
            </button>

            <button
              type="button"
              className="initiation__next"
              disabled={isSubmitting} 
              onClick={handleNext}
            >
              {isSubmitting ? 'Saving...' : 'Next →'}
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default ProcurementAssessmentPage;
